import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTwitter, FaMapMarkerAlt, FaPhoneAlt, FaEnvelope } from "react-icons/fa";
import styled, { keyframes } from "styled-components";

// Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// Styled Components
const FooterContainer = styled.footer`
  background: linear-gradient(135deg, #151c48 0%, #2a3480 100%);
  color: white;
  padding: 60px 20px 30px;
  font-size: 14px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #ffd700, #5ab9d3);
  }

  @media (min-width: 992px) {
    padding: 80px 60px 40px;
  }
`;

const FooterContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FooterSection = styled.div`
  position: relative;
  z-index: 2;
`;

const BrandSection = styled(FooterSection)`
  @media (min-width: 768px) {
    grid-column: span 2;
  }

  @media (min-width: 1200px) {
    grid-column: span 1;
  }
`;

const BrandName = styled.h2`
  font-size: 28px;
  font-weight: 800;
  color: white;
  margin-bottom: 20px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 60px;
    height: 4px;
    background: linear-gradient(90deg, #ffd700, #5ab9d3);
    border-radius: 2px;
  }
`;

const BrandDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  margin-bottom: 25px;
  max-width: 400px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: rgba(255, 255, 255, 0.9);

  svg {
    color: #5ab9d3;
    flex-shrink: 0;
    margin-top: 3px;
  }

  a {
    color: inherit;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #ffd700;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin-bottom: 25px;
  position: relative;
  padding-bottom: 10px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 2px;
    background: linear-gradient(90deg, #ffd700, #5ab9d3);
  }
`;

const FooterLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    transition: transform 0.3s;

    &:hover {
      transform: translateX(5px);
    }

    a {
      text-decoration: none;
      color: rgba(255, 255, 255, 0.8);
      font-size: 15px;
      transition: all 0.3s;
      display: flex;
      align-items: center;
      gap: 8px;

      &::before {
        content: '→';
        color: #5ab9d3;
        font-weight: bold;
        opacity: 0;
        transition: opacity 0.3s;
      }

      &:hover {
        color: white;
        padding-left: 5px;

        &::before {
          opacity: 1;
        }
      }
    }
  }
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 25px;

  a {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 16px;
    transition: all 0.3s;
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.2);

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(90, 185, 211, 0.3));
      opacity: 0;
      transition: opacity 0.3s;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

      &::before {
        opacity: 1;
      }

      &.facebook {
        color: #1877f2;
        background: white;
      }
      &.instagram {
        color: #e4405f;
        background: white;
      }
      &.linkedin {
        color: #0a66c2;
        background: white;
      }
      &.twitter {
        color: #1da1f2;
        background: white;
      }
    }
  }
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 25px;
`;

const NewsletterInput = styled.input`
  padding: 12px 15px;
  border-radius: 8px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 14px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  &:focus {
    outline: none;
    background: rgba(255, 255, 255, 0.2);
    border-color: #5ab9d3;
  }
`;

const SubscribeButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(90deg, #ffd700, #5ab9d3);
  color: #151c48;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  }
`;

const DecorationCircle = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 215, 0, 0.05) 0%, rgba(255, 215, 0, 0) 70%);
  z-index: 1;

  &:nth-child(1) {
    top: -100px;
    right: -100px;
    animation: ${float} 8s ease-in-out infinite;
  }

  &:nth-child(2) {
    bottom: -150px;
    left: -150px;
    animation: ${float} 10s ease-in-out infinite 2s;
  }
`;

const CopyrightContainer = styled.div`
  max-width: 1400px;
  margin: 60px auto 0;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;
  position: relative;
  z-index: 2;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
  }
`;

const CopyrightText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
  margin: 0;

  a {
    color: #5ab9d3;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: #ffd700;
    }
  }
`;

const Footer = () => (
  <FooterContainer>
    <DecorationCircle />
    <DecorationCircle />
    
    <FooterContent>
      <BrandSection>
        <BrandName>Realoca Fácil</BrandName>
        <BrandDescription>
          Facilitamos sua mudança com serviços premium e atendimento personalizado. 
          Nossa equipe especializada garante uma transição suave para seu novo lar.
        </BrandDescription>
        
        <ContactInfo>
          <ContactItem>
            <FaMapMarkerAlt />
            <span>Av. da Liberdade, 100, Lisboa, Portugal</span>
          </ContactItem>
          <ContactItem>
            <FaPhoneAlt />
            <a href="tel:+351123456789">+351 123 456 789</a>
          </ContactItem>
          <ContactItem>
            <FaEnvelope />
            <a href="mailto:contato@relocafacil.com">contato@relocafacil.com</a>
          </ContactItem>
        </ContactInfo>
      </BrandSection>

      <FooterSection>
        <SectionTitle>Links Rápidos</SectionTitle>
        <FooterLinks>
          <li><a href="/about-us">Sobre Nós</a></li>
          {/* <li><a href="/servicos">Nossos Serviços</a></li> */}
          {/* <li><a href="/depoimentos">Depoimentos</a></li> */}
          {/* <li><a href="/blog">Blog</a></li> */}
          <li><a href="/faq">Perguntas Frequentes</a></li>
        </FooterLinks>
      </FooterSection>

      <FooterSection>
        <SectionTitle>Serviços</SectionTitle>
        <FooterLinks>
          <li><a href="/servicos/realocacao">Realocação Residencial</a></li>
          <li><a href="/servicos/corporativa">Realocação Corporativa</a></li>
          <li><a href="/servicos/vistos">Assessoria de Vistos</a></li>
          <li><a href="/servicos/escolas">Matrículas Escolares</a></li>
          <li><a href="/servicos/adaptacao">Programa de Adaptação</a></li>
          <li><a href="/politica-privacidade">Política de Privacidade</a></li>

        </FooterLinks>
      </FooterSection>

      <FooterSection>
        <SectionTitle>Newsletter</SectionTitle>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '20px' }}>
          Assine nossa newsletter para receber dicas e ofertas exclusivas.
        </p>
        
        <NewsletterForm>
          <NewsletterInput type="email" placeholder="Seu melhor email" required />
          <SubscribeButton type="submit">
            Assinar
          </SubscribeButton>
        </NewsletterForm>
        
        <SocialIcons>
          <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer" className="facebook">
            <FaFacebookF />
          </a>
          <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer" className="instagram">
            <FaInstagram />
          </a>
          <a href="https://www.linkedin.com" target="_blank" rel="noopener noreferrer" className="linkedin">
            <FaLinkedinIn />
          </a>
          <a href="https://www.twitter.com" target="_blank" rel="noopener noreferrer" className="twitter">
            <FaTwitter />
          </a>
        </SocialIcons>
      </FooterSection>
    </FooterContent>

    <CopyrightContainer>
      <CopyrightText>
        &copy; {new Date().getFullYear()} Realoca Fácil. Todos os direitos reservados.
      </CopyrightText>
      <CopyrightText>
        <a href="/politica-privacidade">Política de Privacidade</a> | <a href="/termos">Termos de Serviço</a>
      </CopyrightText>
    </CopyrightContainer>
  </FooterContainer>
);

export default Footer;